import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Play, RefreshCw, MessageSquarePlus, CheckCircle2, Reply } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

function severityTone(sev) {
  const s = String(sev || '').toLowerCase();
  if (s === 'critical') return 'bg-rose-500/15 text-rose-200 border-rose-500/30';
  if (s === 'high') return 'bg-amber-500/15 text-amber-200 border-amber-500/30';
  if (s === 'medium') return 'bg-sky-500/15 text-sky-200 border-sky-500/30';
  return 'bg-secondary text-secondary-foreground';
}

function deriveImmediateActions(findings) {
  const out = [];
  const seen = new Set();
  for (const f of findings || []) {
    const scanner = String(f?.scanner || '').toLowerCase();
    if (scanner === 'secrets' && !seen.has('secrets')) {
      seen.add('secrets');
      out.push('Rotate exposed credentials immediately and move secrets to secure env/secret manager.');
    }
    if ((scanner === 'deps' || scanner === 'dependency' || scanner === 'sca') && !seen.has('deps')) {
      seen.add('deps');
      out.push('Patch vulnerable dependencies and pin safe versions in lockfile today.');
    }
    if (scanner === 'sast' && !seen.has('sast')) {
      seen.add('sast');
      out.push('Add server-side input validation + sanitization on all user-controlled paths.');
    }
    if ((scanner === 'web_dast' || scanner === 'dast' || scanner === 'headers') && !seen.has('headers')) {
      seen.add('headers');
      out.push('Enable CSP, HSTS, X-Frame-Options, and secure cookie flags (HttpOnly/SameSite/Secure).');
    }
  }
  return out;
}

function commentTagTone(tag) {
  const t = String(tag || '').toLowerCase();
  if (t === 'accepted_risk') return 'bg-amber-500/15 text-amber-200 border-amber-500/30';
  if (t === 'ready_to_verify') return 'bg-sky-500/15 text-sky-200 border-sky-500/30';
  if (t === 'resolved') return 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30';
  return 'bg-secondary text-secondary-foreground';
}

function fmtTs(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? String(ts) : d.toLocaleString();
}

const DEFAULT_COMMENT_FILTERS = { openOnly: false, findingId: '', statusTag: '' };

function parseResolveRolesEnv() {
  const raw =
    (typeof process !== 'undefined' && process.env && process.env.REACT_APP_DEVGUARD_COMMENT_RESOLVE_ROLES) ||
    'admin,user';
  return new Set(
    String(raw)
      .split(',')
      .map((x) => x.trim().toLowerCase())
      .filter(Boolean)
  );
}

function userMayResolveComments(user) {
  const role = String(user?.role || 'user').toLowerCase();
  return parseResolveRolesEnv().has(role);
}

function buildCommentTree(comments) {
  const list = Array.isArray(comments) ? comments : [];
  const byId = new Map();
  for (const c of list) {
    if (!c?.comment_id) continue;
    byId.set(c.comment_id, { ...c, _children: [] });
  }
  const roots = [];
  for (const c of byId.values()) {
    const pid = c.parent_comment_id;
    if (pid && byId.has(pid)) {
      byId.get(pid)._children.push(c);
    } else {
      roots.push(c);
    }
  }
  const sortFn = (a, b) => {
    const ta = new Date(a.created_at || 0).getTime();
    const tb = new Date(b.created_at || 0).getTime();
    return tb - ta;
  };
  const sortRecursive = (node) => {
    node._children.sort(sortFn);
    node._children.forEach(sortRecursive);
  };
  roots.sort(sortFn);
  roots.forEach(sortRecursive);
  return roots;
}

function CommentMessage({ text }) {
  const s = String(text || '');
  const parts = s.split(/(@[a-zA-Z0-9._+-]+)/g);
  return (
    <p className="mt-2 whitespace-pre-wrap text-sm text-foreground">
      {parts.map((part, i) =>
        part.startsWith('@') ? (
          <span key={`${i}-${part}`} className="rounded bg-primary/15 px-0.5 font-medium text-primary">
            {part}
          </span>
        ) : (
          <span key={`${i}-t`}>{part}</span>
        )
      )}
    </p>
  );
}

function DevGuardCommentBranch({ node, depth, scanId, canResolve, onReply, onResolve, busy }) {
  const men = Array.isArray(node?.mentions) ? node.mentions : [];
  return (
    <div className={depth ? 'mt-2 ml-2 border-l border-border/50 pl-2' : ''}>
      <div className="rounded border border-border/50 bg-background/50 p-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className={commentTagTone(node?.status_tag)}>
            {node?.status_tag || 'needs_engineering'}
          </Badge>
          {node?.finding_id ? (
            <Badge variant="outline" className="text-[10px]">
              finding: {node.finding_id}
            </Badge>
          ) : (
            <Badge variant="secondary" className="text-[10px]">
              scan note
            </Badge>
          )}
          <span className="text-[11px] text-muted-foreground">
            {node?.author || 'unknown'} · {fmtTs(node?.created_at)}
          </span>
          <div className="ml-auto flex flex-wrap items-center gap-1">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-[11px]"
              onClick={() => onReply(node)}
              disabled={busy}
            >
              <Reply className="mr-1 h-3.5 w-3.5" />
              Reply
            </Button>
            {canResolve && !node?.resolved ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-[11px]"
                onClick={() => onResolve(scanId, node.comment_id)}
                disabled={busy}
              >
                <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                Resolve
              </Button>
            ) : null}
            {node?.resolved ? <span className="text-[11px] text-emerald-300/80">Resolved</span> : null}
          </div>
        </div>
        {men.length > 0 ? (
          <p className="mt-1 text-[10px] text-muted-foreground">
            Mentions:{' '}
            {men.map((m) => (
              <span key={m} className="mr-1 font-mono text-primary/90">
                @{m}
              </span>
            ))}
          </p>
        ) : null}
        <CommentMessage text={node?.message} />
      </div>
      {(node._children || []).map((ch) => (
        <DevGuardCommentBranch
          key={ch.comment_id}
          node={ch}
          depth={depth + 1}
          scanId={scanId}
          canResolve={canResolve}
          onReply={onReply}
          onResolve={onResolve}
          busy={busy}
        />
      ))}
    </div>
  );
}

function ScanProvenancePanel({ execution }) {
  if (!execution || typeof execution !== 'object' || Object.keys(execution).length === 0) return null;
  const ex = execution;
  const bySrc = ex.findings_by_source && typeof ex.findings_by_source === 'object' ? ex.findings_by_source : null;
  const versions = ex.tool_versions && typeof ex.tool_versions === 'object' ? ex.tool_versions : null;
  const warnings = Array.isArray(ex.tool_warnings) ? ex.tool_warnings : [];
  return (
    <details className="rounded-md border border-border/60 bg-card/20 p-2 text-xs">
      <summary className="cursor-pointer font-semibold uppercase tracking-wide text-muted-foreground">
        Scan provenance
      </summary>
      <div className="mt-2 space-y-2 text-muted-foreground">
        <p>
          <span className="text-foreground/80">Engine:</span> {ex.engine ?? '—'}
        </p>
        <p>
          <span className="text-foreground/80">Started:</span> {ex.started_at ?? '—'}{' '}
          <span className="text-foreground/80">· Completed:</span> {ex.completed_at ?? '—'}{' '}
          <span className="text-foreground/80">· Duration:</span>{' '}
          {ex.duration_ms != null ? `${ex.duration_ms} ms` : '—'}
        </p>
        <p>
          <span className="text-foreground/80">Clone:</span>{' '}
          {ex.clone_ok === true ? 'ok' : ex.clone_ok === false ? 'failed / skipped' : '—'}
          {ex.text_files_considered != null ? (
            <>
              {' '}
              <span className="text-foreground/80">· Text files scanned (cap):</span> {ex.text_files_considered}
            </>
          ) : null}
        </p>
        {Array.isArray(ex.tools_invoked) && ex.tools_invoked.length > 0 ? (
          <p>
            <span className="text-foreground/80">Steps:</span>{' '}
            {ex.tools_invoked.map((t) => (
              <Badge key={t} variant="outline" className="mr-1 mt-1 text-[10px]">
                {t}
              </Badge>
            ))}
          </p>
        ) : null}
        {versions ? (
          <div>
            <p className="text-foreground/80">Tool versions (API host)</p>
            <pre className="mt-1 max-h-32 overflow-auto rounded border border-border/50 bg-background/60 p-2 font-mono text-[10px] leading-relaxed text-muted-foreground">
              {Object.entries(versions)
                .map(([k, v]) => `${k}: ${v}`)
                .join('\n')}
            </pre>
          </div>
        ) : null}
        {bySrc && Object.keys(bySrc).length > 0 ? (
          <div>
            <p className="text-foreground/80">Findings by source</p>
            <ul className="mt-1 list-disc space-y-0.5 pl-4">
              {Object.entries(bySrc).map(([k, n]) => (
                <li key={k}>
                  {k}: {n}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {warnings.length > 0 ? (
          <div>
            <p className="text-amber-200/90">Tool warnings</p>
            <ul className="mt-1 list-disc space-y-1 pl-4 text-amber-200/80">
              {warnings.map((w, i) => (
                <li key={i} className="break-words">
                  {w}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </details>
  );
}

export function DevGuardPage() {
  const { user } = useAuth();
  const canResolveComments = userMayResolveComments(user);

  const [repoUrl, setRepoUrl] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [scanners, setScanners] = useState('sast,deps,secrets,web_dast,headers,pentest_full');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [runningId, setRunningId] = useState(null);
  const [items, setItems] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [reportByScanId, setReportByScanId] = useState({});
  const [reportLoadingId, setReportLoadingId] = useState(null);
  const [fixPrBusyId, setFixPrBusyId] = useState(null);
  const [fixPrProposalByScanId, setFixPrProposalByScanId] = useState({});
  const [phase2Exploit, setPhase2Exploit] = useState(false);
  const [phase2ApprovalId, setPhase2ApprovalId] = useState('');
  const [phase2ApprovedBy, setPhase2ApprovedBy] = useState('');
  const [phase2ApprovalNote, setPhase2ApprovalNote] = useState('');
  const [phase2Modules, setPhase2Modules] = useState('nmap_vuln,nikto_basic,sqlmap_quick');
  const [phase2Targets, setPhase2Targets] = useState('');
  const [commentsByScanId, setCommentsByScanId] = useState({});
  const [commentsLoadingId, setCommentsLoadingId] = useState(null);
  const [commentSavingId, setCommentSavingId] = useState(null);
  const [commentDraftByScanId, setCommentDraftByScanId] = useState({});
  const [commentFilterByScanId, setCommentFilterByScanId] = useState({});

  const getCommentFilters = (scanId) => ({
    ...DEFAULT_COMMENT_FILTERS,
    ...(commentFilterByScanId[scanId] || {}),
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/operations-hub/devguard/scans?limit=50', { timeoutMs: 30000 });
      setItems(r?.items || []);
    } catch (e) {
      toast.error(e?.message || 'Failed to load DevGuard scans');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createScan = async () => {
    if (!repoUrl.trim() && !websiteUrl.trim()) {
      toast.error('Provide repo URL and/or website URL');
      return;
    }
    setCreating(true);
    try {
      const scannerList = scanners
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean);
      const rec = await api.post(
        '/api/operations-hub/devguard/scans',
        {
          repo_url: repoUrl || null,
          website_url: websiteUrl || null,
          scanners: scannerList,
          audience: 'engineer',
        },
        { timeoutMs: 30000 }
      );
      toast.success('DevGuard scan created', { description: rec?.scan_id });
      await load();
    } catch (e) {
      toast.error(e?.message || 'Failed to create scan');
    } finally {
      setCreating(false);
    }
  };

  const runScan = async (scanId) => {
    setRunningId(scanId);
    try {
      const exploitModules = phase2Modules
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean);
      const pentestTargets = phase2Targets
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean);
      const rec = await api.post(
        `/api/operations-hub/devguard/scans/${encodeURIComponent(scanId)}/run`,
        {
          force: true,
          phase2_exploit: phase2Exploit,
          approval_id: phase2ApprovalId || null,
          approved_by: phase2ApprovedBy || null,
          approval_note: phase2ApprovalNote || null,
          exploit_modules: exploitModules,
          pentest_targets: pentestTargets,
        },
        { timeoutMs: 180000 }
      );
      toast.success('Scan executed', { description: rec?.status || 'updated' });
      await load();
    } catch (e) {
      toast.error(e?.message || 'Failed to run scan');
    } finally {
      setRunningId(null);
    }
  };

  const loadReport = async (scanId) => {
    setReportLoadingId(scanId);
    try {
      const rep = await api.get(`/api/operations-hub/devguard/scans/${encodeURIComponent(scanId)}/report`, {
        timeoutMs: 30000,
      });
      setReportByScanId((prev) => ({ ...prev, [scanId]: rep }));
    } catch (e) {
      toast.error(e?.message || 'Failed to load scan report');
    } finally {
      setReportLoadingId(null);
    }
  };

  const loadCommentsForScan = async (scanId, filterOverride) => {
    const f = filterOverride ?? getCommentFilters(scanId);
    setCommentsLoadingId(scanId);
    try {
      const params = new URLSearchParams();
      if (f.openOnly) params.set('include_resolved', 'false');
      if (f.findingId) params.set('finding_id', f.findingId);
      if (f.statusTag) params.set('status_tag', f.statusTag);
      const q = params.toString();
      const path = `/api/operations-hub/devguard/scans/${encodeURIComponent(scanId)}/comments${q ? `?${q}` : ''}`;
      const r = await api.get(path, { timeoutMs: 30000 });
      setCommentsByScanId((prev) => ({ ...prev, [scanId]: r?.items || [] }));
    } catch (e) {
      toast.error(e?.message || 'Failed to load collaboration comments');
    } finally {
      setCommentsLoadingId(null);
    }
  };

  const patchCommentFilters = (scanId, patch) => {
    setCommentFilterByScanId((prev) => {
      const cur = { ...DEFAULT_COMMENT_FILTERS, ...(prev[scanId] || {}) };
      const next = { ...cur, ...patch };
      queueMicrotask(() => {
        void loadCommentsForScan(scanId, next);
      });
      return { ...prev, [scanId]: next };
    });
  };

  const updateDraft = (scanId, patch) => {
    setCommentDraftByScanId((prev) => ({
      ...prev,
      [scanId]: {
        message: '',
        finding_id: '',
        status_tag: 'needs_engineering',
        parent_comment_id: null,
        ...(prev[scanId] || {}),
        ...patch,
      },
    }));
  };

  const handleReplyToComment = (scanId, node) => {
    updateDraft(scanId, {
      parent_comment_id: node.comment_id,
      finding_id: node.finding_id || '',
    });
    toast.message('Reply to thread', { description: 'Compose your reply below.' });
  };

  const submitComment = async (scan) => {
    const scanId = scan?.scan_id;
    if (!scanId) return;
    const d = commentDraftByScanId[scanId] || {};
    const message = String(d.message || '').trim();
    if (!message) {
      toast.error('Write a comment first');
      return;
    }
    setCommentSavingId(scanId);
    try {
      await api.post(
        `/api/operations-hub/devguard/scans/${encodeURIComponent(scanId)}/comments`,
        {
          message,
          finding_id: d.finding_id || null,
          parent_comment_id: d.parent_comment_id || null,
          status_tag: d.status_tag || 'needs_engineering',
        },
        { timeoutMs: 30000 }
      );
      updateDraft(scanId, { message: '', parent_comment_id: null });
      await loadCommentsForScan(scanId);
      toast.success('Comment added');
    } catch (e) {
      toast.error(e?.message || 'Failed to add comment');
    } finally {
      setCommentSavingId(null);
    }
  };

  const markCommentResolved = async (scanId, commentId) => {
    setCommentSavingId(scanId);
    try {
      await api.patch(`/api/operations-hub/devguard/scans/${encodeURIComponent(scanId)}/comments/${encodeURIComponent(commentId)}`, {
        resolved: true,
        status_tag: 'resolved',
      });
      await loadCommentsForScan(scanId);
      toast.success('Comment marked resolved');
    } catch (e) {
      toast.error(e?.message || 'Failed to update comment');
    } finally {
      setCommentSavingId(null);
    }
  };

  const requestFixPr = async (scanId) => {
    setFixPrBusyId(scanId);
    try {
      const r = await api.post(
        `/api/operations-hub/devguard/scans/${encodeURIComponent(scanId)}/fix-pr`,
        {},
        { timeoutMs: 90000 }
      );
      if (r?.mode === 'github_draft_pr' && r?.pr_url) {
        toast.success('Draft fix PR opened', { description: r.pr_url });
        window.open(r.pr_url, '_blank', 'noopener,noreferrer');
      } else if (r?.mode === 'proposal_only') {
        toast.message('Fix proposal generated', { description: r?.message || 'Saved server-side; copy markdown below.' });
        if (r?.proposal_markdown) {
          setFixPrProposalByScanId((prev) => ({ ...prev, [scanId]: r.proposal_markdown }));
        }
      } else {
        toast.success('Fix workflow updated', { description: r?.message || 'OK' });
      }
      await load();
      await loadReport(scanId);
    } catch (e) {
      toast.error(e?.message || 'Fix PR request failed');
    } finally {
      setFixPrBusyId(null);
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="relative overflow-hidden rounded-2xl border border-primary/25 bg-gradient-to-br from-card via-card to-primary/[0.06] p-6">
        <div className="relative flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              <Badge variant="outline" className="border-primary/40 text-primary">
                DevGuard
              </Badge>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground">Founder / Engineer Security Workflow</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Create and run product security scans for repos and websites using the live DevGuard engine.
            </p>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" asChild>
              <Link to="/operations">Operations Hub</Link>
            </Button>
            <Button type="button" variant="outline" onClick={load} disabled={loading}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <Card className="border-primary/25 bg-primary/[0.06]">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">How live scanning works</CardTitle>
          <CardDescription>
            DevGuard runs live checks against the provided repository and website targets, including active recon.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm leading-relaxed text-muted-foreground">
          <p>
            Each run performs shallow <span className="font-mono text-xs">git clone</span>, then executes
            <span className="font-medium text-foreground"> Gitleaks</span> + secrets heuristics,
            <span className="font-medium text-foreground"> Semgrep</span> + SAST pattern analysis, dependency vulnerability
            lookups via <span className="font-medium text-foreground">OSV</span>, website HTTP security header review,
            and active pentest recon profiles via the AERTAX backend when enabled. That takes longer and yields
            evidence fields where applicable.
          </p>
          <p>
            <span className="font-medium text-foreground">Direct fixes in the repo</span> today mean an optional{' '}
            <span className="font-medium text-foreground">draft GitHub pull request</span> carrying a structured fix plan
            (markdown), when you enable the server-side CodeFix settings and repo policy your operator documents—not silent
            rewrites of the whole codebase. Broader auto-patch flows would be explicit opt-in with review gates.
          </p>
          <p>
            <span className="font-medium text-foreground">Phase-2 exploit modules</span> are explicit opt-in and require
            approval metadata on each run. Backend env gates still decide whether nikto/sqlmap/nmap-vuln execution is
            permitted in your environment.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Create scan</CardTitle>
          <CardDescription>Queue a repo/website scan contract for DevGuard.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2">
          <input
            className="rounded-md border border-border bg-background px-3 py-2 text-sm"
            placeholder="Repository URL (optional)"
            value={repoUrl}
            onChange={(e) => setRepoUrl(e.target.value)}
          />
          <input
            className="rounded-md border border-border bg-background px-3 py-2 text-sm"
            placeholder="Website URL (optional)"
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
          />
          <input
            className="rounded-md border border-border bg-background px-3 py-2 text-sm md:col-span-2"
            placeholder="Scanners CSV (e.g. sast,deps,secrets,web_dast,headers,pentest_full)"
            value={scanners}
            onChange={(e) => setScanners(e.target.value)}
          />
          <label className="flex items-center gap-2 text-sm md:col-span-2">
            <input
              type="checkbox"
              checked={phase2Exploit}
              onChange={(e) => setPhase2Exploit(e.target.checked)}
            />
            Enable phase-2 exploit modules (requires approval metadata and backend env gate)
          </label>
          <input
            className="rounded-md border border-border bg-background px-3 py-2 text-sm"
            placeholder="Approval ID (required for phase-2)"
            value={phase2ApprovalId}
            onChange={(e) => setPhase2ApprovalId(e.target.value)}
          />
          <input
            className="rounded-md border border-border bg-background px-3 py-2 text-sm"
            placeholder="Approved by (email/name)"
            value={phase2ApprovedBy}
            onChange={(e) => setPhase2ApprovedBy(e.target.value)}
          />
          <input
            className="rounded-md border border-border bg-background px-3 py-2 text-sm md:col-span-2"
            placeholder="Approval note / scope justification"
            value={phase2ApprovalNote}
            onChange={(e) => setPhase2ApprovalNote(e.target.value)}
          />
          <input
            className="rounded-md border border-border bg-background px-3 py-2 text-sm"
            placeholder="Phase-2 modules CSV (nmap_vuln,nikto_basic,sqlmap_quick)"
            value={phase2Modules}
            onChange={(e) => setPhase2Modules(e.target.value)}
          />
          <input
            className="rounded-md border border-border bg-background px-3 py-2 text-sm"
            placeholder="Pentest targets CSV (optional host/IP list)"
            value={phase2Targets}
            onChange={(e) => setPhase2Targets(e.target.value)}
          />
          <div className="md:col-span-2">
            <Button type="button" onClick={createScan} disabled={creating}>
              {creating ? 'Creating…' : 'Create DevGuard Scan'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Scans</CardTitle>
          <CardDescription>
            Recent DevGuard scans for your tenant. Expand a scan to view vulnerabilities, analysis, and concrete fixes.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {(items || []).map((x) => (
            <div key={x.scan_id} className="rounded-lg border border-border/70 bg-background/40 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-medium text-foreground">{x.scan_id}</div>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary">{x.status || 'unknown'}</Badge>
                  <Badge variant="outline">{x.adapter_mode || 'live'}</Badge>
                </div>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {x.targets?.repo_url || '—'} {x.targets?.website_url ? `| ${x.targets.website_url}` : ''}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {(x.scanners || []).slice(0, 6).map((s) => (
                  <Badge key={s} variant="outline" className="text-[10px]">
                    {s}
                  </Badge>
                ))}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setExpandedId((v) => {
                      const next = v === x.scan_id ? null : x.scan_id;
                      if (next && !reportByScanId[x.scan_id]) {
                        void loadReport(x.scan_id);
                      }
                      if (next && !commentsByScanId[x.scan_id]) {
                        void loadCommentsForScan(x.scan_id);
                      }
                      return next;
                    });
                  }}
                >
                  {expandedId === x.scan_id ? 'Hide details' : 'View details'}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="ml-auto"
                  disabled={runningId === x.scan_id}
                  onClick={() => runScan(x.scan_id)}
                >
                  <Play className="mr-1 h-4 w-4" />
                  {runningId === x.scan_id ? 'Running…' : 'Run'}
                </Button>
              </div>
              {x.result?.summary ? (
                <p className="mt-2 text-xs text-muted-foreground">
                  Findings: {x.result.summary.findings_total ?? 0} · High: {x.result.summary.by_severity?.high ?? 0} ·
                  Medium: {x.result.summary.by_severity?.medium ?? 0}
                </p>
              ) : null}
              {expandedId === x.scan_id ? (
                <div className="mt-3 space-y-3 border-t border-border/50 pt-3">
                  <ScanProvenancePanel execution={x.result?.execution} />
                  {reportLoadingId === x.scan_id && !reportByScanId[x.scan_id] ? (
                    <p className="text-xs text-muted-foreground">Loading report…</p>
                  ) : null}
                  {reportByScanId[x.scan_id] ? (
                    <div className="space-y-3 rounded-md border border-border/60 bg-card/30 p-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Analysis report
                      </p>
                      <p className="text-sm text-foreground">
                        Risk score:{' '}
                        <span className="font-semibold">
                          {reportByScanId[x.scan_id]?.summary?.risk_score_0_100 ?? 0}/100
                        </span>{' '}
                        · findings {reportByScanId[x.scan_id]?.summary?.findings_total ?? 0}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Auto-fix status: {reportByScanId[x.scan_id]?.repo_autofix?.status || 'unknown'}
                      </p>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          Immediate fix plan
                        </p>
                        <div className="mt-2 space-y-2">
                          {(reportByScanId[x.scan_id]?.immediate_fixes || []).map((fix, idx) => (
                            <div key={`${x.scan_id}-fix-${idx}`} className="rounded-md border border-border/50 bg-background/40 p-2">
                              <p className="text-xs font-semibold text-foreground">
                                {fix?.priority || 'P?'} · {fix?.title || 'Fix'}
                              </p>
                              <ul className="mt-1 list-disc space-y-1 pl-4 text-xs text-muted-foreground">
                                {(fix?.actions || []).map((a) => (
                                  <li key={a}>{a}</li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          Improvement backlog
                        </p>
                        <ul className="mt-1 list-disc space-y-1 pl-4 text-xs text-muted-foreground">
                          {(reportByScanId[x.scan_id]?.improvement_backlog || []).map((item) => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      </div>
                      {reportByScanId[x.scan_id]?.repo_autofix?.fix_pr_eligible ? (
                        <div className="space-y-2 border-t border-border/40 pt-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Repository fix workflow
                          </p>
                          {!reportByScanId[x.scan_id]?.repo_autofix?.fix_pr_github_ready ? (
                            <p className="text-[11px] text-muted-foreground">
                              {reportByScanId[x.scan_id]?.repo_autofix?.fix_pr_repo_policy_error ||
                                reportByScanId[x.scan_id]?.repo_autofix?.fix_pr_codefix_reason ||
                                'Configure AERTA_CODEFIX_PR_ENABLED, token, and repo policy to open a real GitHub draft PR.'}
                            </p>
                          ) : null}
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={fixPrBusyId === x.scan_id}
                            onClick={() => void requestFixPr(x.scan_id)}
                          >
                            {fixPrBusyId === x.scan_id
                              ? 'Working…'
                              : reportByScanId[x.scan_id]?.repo_autofix?.fix_pr_github_ready
                                ? 'Open draft fix PR (GitHub)'
                                : 'Generate fix proposal (save)'}
                          </Button>
                          {x.fix_pr_attempts?.[0]?.pr_url ? (
                            <p className="text-xs">
                              <a
                                href={x.fix_pr_attempts[0].pr_url}
                                className="text-primary underline"
                                target="_blank"
                                rel="noreferrer"
                              >
                                Latest draft PR
                              </a>
                            </p>
                          ) : null}
                          {fixPrProposalByScanId[x.scan_id] ? (
                            <details className="text-xs">
                              <summary className="cursor-pointer text-muted-foreground">Proposal markdown (copy)</summary>
                              <pre className="mt-2 max-h-48 overflow-auto rounded border border-border/60 bg-background/80 p-2 font-mono text-[10px] whitespace-pre-wrap">
                                {fixPrProposalByScanId[x.scan_id]}
                              </pre>
                            </details>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="space-y-2 rounded-md border border-border/60 bg-card/30 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Collaboration notes
                      </p>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => void loadCommentsForScan(x.scan_id)}
                        disabled={commentsLoadingId === x.scan_id}
                      >
                        <MessageSquarePlus className="mr-1 h-4 w-4" />
                        {commentsLoadingId === x.scan_id ? 'Loading…' : 'Refresh comments'}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Add context for engineering, product, or compliance teams. Mention people with @handle (highlighted
                      below). Email notifications are not wired yet.
                    </p>
                    {!canResolveComments ? (
                      <p className="text-[11px] text-muted-foreground">
                        Marking threads resolved is limited to roles allowed by the server (default: admin and user).
                      </p>
                    ) : null}
                    <div className="rounded border border-border/50 bg-background/30 p-2">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Filter thread
                      </p>
                      <div className="mt-2 grid gap-2 md:grid-cols-4">
                        <label className="flex items-center gap-2 text-[11px] text-muted-foreground">
                          <input
                            type="checkbox"
                            checked={!!getCommentFilters(x.scan_id).openOnly}
                            onChange={(e) => patchCommentFilters(x.scan_id, { openOnly: e.target.checked })}
                          />
                          Open only
                        </label>
                        <select
                          className="rounded-md border border-border bg-background px-2 py-2 text-xs"
                          value={getCommentFilters(x.scan_id).findingId}
                          onChange={(e) => patchCommentFilters(x.scan_id, { findingId: e.target.value })}
                        >
                          <option value="">All findings</option>
                          {(x.result?.findings || []).slice(0, 100).map((f, idx) => {
                            const fid = f?.finding_id || `finding-${idx + 1}`;
                            const title = f?.title || fid;
                            return (
                              <option key={`${x.scan_id}-flt-${fid}-${idx}`} value={fid}>
                                {title.slice(0, 72)}
                              </option>
                            );
                          })}
                        </select>
                        <select
                          className="rounded-md border border-border bg-background px-2 py-2 text-xs md:col-span-2"
                          value={getCommentFilters(x.scan_id).statusTag}
                          onChange={(e) => patchCommentFilters(x.scan_id, { statusTag: e.target.value })}
                        >
                          <option value="">All statuses</option>
                          <option value="needs_engineering">Needs engineering</option>
                          <option value="accepted_risk">Accepted risk</option>
                          <option value="ready_to_verify">Ready to verify</option>
                          <option value="resolved">Resolved</option>
                        </select>
                      </div>
                    </div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">New note</p>
                    <div className="grid gap-2 md:grid-cols-3">
                      <select
                        className="rounded-md border border-border bg-background px-2 py-2 text-xs"
                        value={(commentDraftByScanId[x.scan_id]?.finding_id || '')}
                        onChange={(e) => updateDraft(x.scan_id, { finding_id: e.target.value })}
                      >
                        <option value="">General scan note</option>
                        {(x.result?.findings || []).slice(0, 100).map((f, idx) => {
                          const fid = f?.finding_id || `finding-${idx + 1}`;
                          const title = f?.title || fid;
                          return (
                            <option key={`${x.scan_id}-scope-${fid}-${idx}`} value={fid}>
                              {title.slice(0, 90)}
                            </option>
                          );
                        })}
                      </select>
                      <select
                        className="rounded-md border border-border bg-background px-2 py-2 text-xs"
                        value={(commentDraftByScanId[x.scan_id]?.status_tag || 'needs_engineering')}
                        onChange={(e) => updateDraft(x.scan_id, { status_tag: e.target.value })}
                      >
                        <option value="needs_engineering">Needs engineering</option>
                        <option value="accepted_risk">Accepted risk</option>
                        <option value="ready_to_verify">Ready to verify</option>
                        {canResolveComments ? <option value="resolved">Resolved</option> : null}
                      </select>
                    </div>
                    {commentDraftByScanId[x.scan_id]?.parent_comment_id ? (
                      <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-primary/25 bg-primary/[0.06] px-2 py-1 text-[11px] text-muted-foreground">
                        <span>Replying to a thread</span>
                        <button
                          type="button"
                          className="text-primary underline"
                          onClick={() => updateDraft(x.scan_id, { parent_comment_id: null })}
                        >
                          Cancel reply
                        </button>
                      </div>
                    ) : null}
                    <textarea
                      className="min-h-[72px] w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                      placeholder="Add context, business impact, or implementation notes for this finding/scan."
                      value={(commentDraftByScanId[x.scan_id]?.message || '')}
                      onChange={(e) => updateDraft(x.scan_id, { message: e.target.value })}
                    />
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => void submitComment(x)}
                        disabled={commentSavingId === x.scan_id}
                      >
                        {commentSavingId === x.scan_id ? 'Saving…' : 'Add comment'}
                      </Button>
                    </div>
                    {(() => {
                      const tree = buildCommentTree(commentsByScanId[x.scan_id] || []);
                      if (tree.length === 0) {
                        return (
                          <p className="text-xs text-muted-foreground">
                            No collaboration notes match these filters yet. Add a note or adjust filters above.
                          </p>
                        );
                      }
                      return (
                        <div className="space-y-2">
                          {tree.map((node) => (
                            <DevGuardCommentBranch
                              key={node.comment_id}
                              node={node}
                              depth={0}
                              scanId={x.scan_id}
                              canResolve={canResolveComments}
                              onReply={(n) => handleReplyToComment(x.scan_id, n)}
                              onResolve={markCommentResolved}
                              busy={commentSavingId === x.scan_id}
                            />
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                  {x.result?.findings?.length ? (
                    <>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          Immediate actions
                        </p>
                        <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-foreground">
                          {deriveImmediateActions(x.result.findings).map((a) => (
                            <li key={a}>{a}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="space-y-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          Vulnerabilities and recommendations
                        </p>
                        {x.result.findings.map((f, idx) => (
                          <div key={`${x.scan_id}-${idx}`} className="rounded-md border border-border/60 bg-card/40 p-3">
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge className={severityTone(f?.severity)}>{f?.severity || 'unknown'}</Badge>
                              <Badge variant="outline">{f?.scanner || 'scanner'}</Badge>
                            </div>
                            <p className="mt-2 text-sm font-medium text-foreground">{f?.title || 'Finding'}</p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              Recommendation: {f?.recommendation || 'No recommendation available.'}
                            </p>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Live execution mode: findings are generated from real repository and website checks run by the
                        DevGuard engine.
                      </p>
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      No detailed findings yet. Run this scan first, then open details to review vulnerabilities and fixes.
                    </p>
                  )}
                </div>
              ) : null}
            </div>
          ))}
          {!loading && (!items || items.length === 0) ? (
            <p className="text-sm text-muted-foreground">No scans yet.</p>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

